import { useEffect, useMemo, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { Search, Trash2, ShoppingBag, ChevronUp, Minus, Plus, UtensilsCrossed, Bike, ChefHat } from "lucide-react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Sheet, SheetContent, SheetTitle } from "@/components/ui/sheet";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, useApi, errMsg } from "@/pos/lib/api";
import { rm } from "@/pos/lib/format";
import CheckoutDialog from "@/pos/components/CheckoutDialog";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { useAuth } from "@/pos/context/AuthContext";
import PosMenuDialog from "@/pos/components/PosMenuDialog";
import { printKitchen } from "@/pos/lib/print";

function ProductCard({ p, onPick }) {
  return (
    <button onClick={() => onPick(p)} data-testid={`pos-product-card-${p.id}`}
      className="group overflow-hidden rounded-2xl border border-red-100 bg-white text-left transition-[box-shadow,border-color] hover:border-red-300 hover:shadow-md">
      <div className="relative aspect-[4/3] overflow-hidden bg-slate-100">
        {p.image ? <img src={p.image} alt={p.name} className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" />
          : <div className="grid h-full place-items-center bg-gradient-to-br from-red-50 to-red-100 font-heading text-3xl font-extrabold text-red-700/60">{p.name.slice(0, 2)}</div>}
        {p.options?.length > 0 && <span className="absolute left-2 top-2 rounded-full bg-white/85 px-2 py-0.5 text-[11px] font-bold text-slate-700 backdrop-blur-md">{p.options.length} pilihan</span>}
      </div>
      <div className="p-3">
        <div className="truncate text-sm font-semibold">{p.name}</div>
        <div className="font-mono text-sm font-bold text-red-700">{rm(p.price)}</div>
      </div>
    </button>
  );
}

const TYPES = [["dine_in", "Makan Sini", UtensilsCrossed], ["takeaway", "Bungkus", ShoppingBag], ["delivery", "Delivery", Bike]];

function OrderMeta({ meta, setMeta, tables }) {
  const set = (k) => (e) => setMeta({ ...meta, [k]: e?.target ? e.target.value : e });
  return (
    <div className="space-y-2 border-b border-slate-200 p-3">
      <div className="grid grid-cols-3 gap-1 rounded-xl bg-slate-100 p-1 text-xs font-semibold">
        {TYPES.map(([k, l, I]) => (
          <button key={k} onClick={() => setMeta({ ...meta, type: k })} data-testid={`order-type-${k}`}
            className={`flex items-center justify-center gap-1 rounded-lg py-2 transition-colors ${meta.type === k ? "bg-white text-red-700 shadow-sm" : "text-slate-500"}`}><I size={14} />{l}</button>
        ))}
      </div>
      {meta.type === "dine_in" ? (
        <div className="grid grid-cols-2 gap-2">
          <Select value={meta.table_id || ""} onValueChange={set("table_id")}>
            <SelectTrigger data-testid="order-table-select"><SelectValue placeholder="Pilih meja" /></SelectTrigger>
            <SelectContent>{tables.map((t) => <SelectItem key={t.id} value={t.id}>Meja {t.number}{t.state === "occupied" ? " (digunakan)" : t.state === "reserved" ? " (ditempah)" : ""}</SelectItem>)}</SelectContent>
          </Select>
          <Input type="number" value={meta.guests} onChange={set("guests")} placeholder="Tetamu" data-testid="order-guests-input" />
        </div>
      ) : (
        <div className="grid grid-cols-2 gap-2">
          <Input value={meta.customer_name} onChange={set("customer_name")} placeholder="Nama pelanggan" data-testid="order-customer-name-input" />
          <Input value={meta.customer_phone} onChange={set("customer_phone")} placeholder="No. telefon" data-testid="order-customer-phone-input" />
          {meta.type === "delivery" && <Input className="col-span-2" value={meta.note} onChange={set("note")} placeholder="Alamat penghantaran" data-testid="order-address-input" />}
        </div>
      )}
    </div>
  );
}

function CartLine({ c, i, update, remove }) {
  return (
    <div className="rounded-lg px-2 py-2 hover:bg-slate-50" data-testid="pos-cart-item-row">
      <div className="flex items-center gap-2">
        <div className="min-w-0 flex-1"><div className="truncate text-sm font-semibold">{c.product.name}</div><div className="font-mono text-xs text-slate-500">{rm(c.product.price)}</div></div>
        {(
          <div className="flex items-center gap-1">
            <button onClick={() => update(i, { qty: Math.max(1, c.qty - 1) })} className="icon-btn border border-slate-200" data-testid={`cart-dec-${i}`}><Minus size={13} /></button>
            <span className="w-6 text-center font-mono text-sm font-bold" data-testid={`cart-qty-${i}`}>{c.qty}</span>
            <button onClick={() => update(i, { qty: c.qty + 1 })} className="icon-btn border border-slate-200" data-testid={`cart-inc-${i}`}><Plus size={13} /></button>
          </div>
        )}
        <div className="w-20 text-right font-mono text-sm font-bold">{rm(c.qty * c.product.price)}</div>
        <button onClick={() => remove(i)} className="rounded p-1 text-slate-400 hover:text-rose-600" data-testid={`cart-remove-${i}`}><Trash2 size={15} /></button>
      </div>
      <input value={c.note} onChange={(e) => update(i, { note: e.target.value })} placeholder="+ nota (cth: kurang pedas)" className="mt-1 w-full bg-transparent text-xs text-amber-700 outline-none placeholder:text-slate-400" data-testid={`cart-note-${i}`} />
    </div>
  );
}

function Cart({ cart, setCart, subtotal, meta, setMeta, tables, onSend, onDirect, busy }) {
  const update = (i, patch) => setCart(cart.map((c, j) => (j === i ? { ...c, ...patch } : c)));
  const remove = (i) => setCart(cart.filter((_, j) => j !== i));
  const canSend = cart.length && (meta.type !== "dine_in" || meta.table_id);
  return (
    <div className="flex h-full flex-col">
      <div className="flex items-center justify-between border-b border-slate-200 p-4 pr-14 lg:pr-4">
        <h3 className="font-heading text-lg font-bold">Order</h3>
        {cart.length > 0 && <button onClick={() => setCart([])} className="text-xs font-medium text-rose-600" data-testid="cart-clear-btn">Kosongkan</button>}
      </div>
      <OrderMeta meta={meta} setMeta={setMeta} tables={tables} />
      <div className="flex-1 overflow-y-auto p-2">
        {cart.length === 0 ? <div className="grid h-full place-items-center py-10 text-center text-sm text-slate-400"><div><ShoppingBag className="mx-auto mb-2" />Pilih menu untuk mula</div></div> :
          cart.map((c, i) => <CartLine key={i} c={c} i={i} update={update} remove={remove} />)}
      </div>
      <div className="border-t border-slate-200 p-4">
        <div className="mb-3 flex items-baseline justify-between">
          <span className="text-sm text-slate-500">Jumlah</span>
          <span className="font-mono text-3xl font-bold" data-testid="pos-cart-total-amount">{rm(subtotal)}</span>
        </div>
        <button disabled={!canSend || busy} onClick={onSend} className="btn-primary h-12 w-full text-base disabled:opacity-40" data-testid="pos-send-kitchen-btn"><ChefHat size={18} /> {busy ? "Menghantar..." : "Hantar ke Dapur"}</button>
        <button disabled={!cart.length} onClick={onDirect} className="mt-2 w-full text-xs font-medium text-slate-500 hover:text-red-700 disabled:opacity-40" data-testid="pos-checkout-btn">Jual terus tanpa dapur (bayar sekarang)</button>
      </div>
    </div>
  );
}

function OptionDialog({ product, onClose, onPick }) {
  return (
    <Dialog open={!!product} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-sm" data-testid="option-dialog">
        <DialogHeader><DialogTitle className="font-heading">{product?.name}</DialogTitle></DialogHeader>
        <div className="grid gap-2">
          {(product?.options || []).map((o) => (
            <button key={o.name} onClick={() => onPick(product, o)} data-testid={`option-${o.name}`}
              className="flex items-center justify-between rounded-xl border-2 border-slate-200 px-4 py-3 text-left font-semibold transition-colors hover:border-red-500 hover:bg-red-50">
              <span>{o.name}</span><span className="font-mono text-sm text-red-700">{rm(product.price + (o.price_delta || 0))}</span>
            </button>
          ))}
          <button onClick={() => onPick(product, null)} data-testid="option-none"
            className="flex items-center justify-between rounded-xl border-2 border-dashed border-slate-200 px-4 py-3 text-left font-semibold text-slate-600 transition-colors hover:border-red-500 hover:bg-red-50">
            <span>Biasa (tanpa pilihan)</span><span className="font-mono text-sm text-red-700">{rm(product?.price || 0)}</span>
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

const BLANK_META = { type: "dine_in", table_id: "", guests: "", customer_name: "", customer_phone: "", note: "" };

export default function POS() {
  const { data: products, reload } = useApi("/products");
  const { data: tables } = useApi("/tables");
  const [params] = useSearchParams();
  const nav = useNavigate();
  const [search, setSearch] = useState("");
  const [cat, setCat] = useState("Semua");
  const [picked, setPicked] = useState(null);
  const [cart, setCart] = useState([]);
  const [meta, setMeta] = useState({ ...BLANK_META, table_id: params.get("table") || "" });
  const [checkout, setCheckout] = useState(false);
  const [payOrder, setPayOrder] = useState(null);
  const [mobileCart, setMobileCart] = useState(false);
  const [busy, setBusy] = useState(false);
  const [addMenu, setAddMenu] = useState(false);
  const { isOwner } = useAuth();
  useEffect(() => { if (params.get("table")) setMeta((m) => ({ ...m, type: "dine_in", table_id: params.get("table") })); }, [params]);

  const active = (products || []).filter((p) => p.active);
  const cats = ["Semua", ...new Set(active.map((p) => p.category))];
  const list = useMemo(() => active.filter((p) => (cat === "Semua" || p.category === cat) && p.name.toLowerCase().includes(search.toLowerCase())), [active, cat, search]);
  const subtotal = Math.round(cart.reduce((s, c) => s + Math.round(c.qty * c.product.price * 100) / 100, 0) * 100) / 100;

  const addLine = (p, opt) => {
    const option = opt?.name || "";
    const product = { id: p.id, name: option ? `${p.name} (${option})` : p.name, price: Math.round((p.price + (opt?.price_delta || 0)) * 100) / 100 };
    setCart((c) => {
      const i = c.findIndex((x) => x.product.id === p.id && x.option === option && !x.note);
      return i >= 0 ? c.map((x, j) => (j === i ? { ...x, qty: x.qty + 1 } : x)) : [...c, { product, option, qty: 1, note: "" }];
    });
    setPicked(null);
  };
  const pick = (p) => (p.options?.length ? setPicked(p) : addLine(p, null));
  const send = async () => {
    setBusy(true);
    try {
      const r = await api.post("/orders", { ...meta, guests: +meta.guests || 0, table_id: meta.type === "dine_in" ? meta.table_id : null,
        items: cart.map((c) => ({ product_id: c.product.id, option: c.option, qty: c.qty, note: c.note })) });
      toast.success(`Order #${r.data.number} dihantar ke dapur`);
      printKitchen(r.data);
      setCart([]); setMobileCart(false); reload();
      if (meta.type === "dine_in") nav("/pos/meja"); else { setPayOrder(r.data); setMeta(BLANK_META); }
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const cartProps = { cart, setCart, subtotal, meta, setMeta, tables: tables || [], onSend: send, busy, onDirect: () => { setMobileCart(false); setCheckout(true); } };

  return (
    <div className="lg:grid lg:h-screen lg:grid-cols-12">
      <section className="pos-section flex flex-col p-4 pb-24 sm:p-6 lg:col-span-8 lg:h-screen lg:overflow-hidden lg:pb-6">
        <div className="mb-3 flex gap-2">
        <div className="relative flex-1">
          <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Cari menu..." className="h-11 bg-white pl-10" data-testid="pos-search-input" />
        </div>
        {isOwner && <button onClick={() => setAddMenu(true)} data-testid="pos-add-menu-btn" className="btn-primary h-11 shrink-0 px-4 text-sm"><Plus size={16} /> Tambah Menu</button>}
        </div>
        <div className="no-scrollbar mb-4 flex gap-2 overflow-x-auto" data-testid="pos-category-filter">
          {cats.map((c) => (
            <button key={c} onClick={() => setCat(c)} data-testid={`pos-category-${c}`}
              className={`shrink-0 rounded-full px-4 py-1.5 text-sm font-medium transition-colors ${cat === c ? "bg-[#DC2626] text-white" : "border border-red-100 bg-white text-slate-600 hover:bg-red-50"}`}>{c}</button>
          ))}
        </div>
        <div className="grid min-h-0 flex-1 auto-rows-max grid-cols-2 content-start gap-3 overflow-y-auto sm:grid-cols-3 xl:grid-cols-4">
          {list.map((p) => <ProductCard key={p.id} p={p} onPick={pick} />)}
        </div>
      </section>
      <aside className="hidden border-l border-red-100 bg-white lg:col-span-4 lg:block lg:h-screen"><Cart {...cartProps} /></aside>
      <button onClick={() => setMobileCart(true)} data-testid="mobile-cart-bar"
        className="pos-cart-bar fixed inset-x-4 bottom-[84px] z-20 flex items-center justify-between rounded-2xl bg-[#2A0707] px-5 py-3.5 text-white shadow-xl lg:hidden">
        <span className="flex items-center gap-2 text-sm font-semibold"><ChevronUp size={18} /> Order ({cart.length})</span>
        <span className="font-mono text-lg font-bold">{rm(subtotal)}</span>
      </button>
      <Sheet open={mobileCart} onOpenChange={setMobileCart}>
        <SheetContent side="bottom" className="h-[88vh] rounded-t-2xl p-0">
          <SheetTitle className="sr-only">Order</SheetTitle>
          <Cart {...cartProps} />
        </SheetContent>
      </Sheet>
      {addMenu && <PosMenuDialog categories={cats.filter((c) => c !== "Semua")} onClose={() => setAddMenu(false)} onSaved={() => { setAddMenu(false); reload(); }} />}
      <OptionDialog product={picked} onClose={() => setPicked(null)} onPick={addLine} />
      <CheckoutDialog open={checkout} onClose={() => setCheckout(false)} cart={cart} subtotal={subtotal} onDone={() => { setCart([]); reload(); }} />
      <CheckoutDialog open={!!payOrder} onClose={() => setPayOrder(null)} orderIds={payOrder ? [payOrder.id] : null}
        cart={[]} subtotal={payOrder?.total || 0} onDone={reload} />
    </div>
  );
}
